/**
 * Web Vitals Standalone Entrypoint
 * Sends Core Web Vitals metrics to Google Analytics 4 via gtag
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'

// Helper function to send metrics to Google Analytics via gtag
function sendToGoogleAnalytics(metric) {
  // Ensure gtag is available
  if (typeof window.gtag === 'function') {
    const value = Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value)
    
    // Send Web Vitals metric as a custom event
    window.gtag('event', 'web_vitals_metric', {
      custom_map: {
        metric_1: 'metric_name',
        metric_2: 'metric_value',
        metric_3: 'metric_rating'
      },
      metric_name: metric.name,
      metric_id: metric.id,
      metric_value: value,
      metric_rating: metric.rating,
      metric_delta: metric.delta,
      page_location: window.location.href,
      page_title: document.title,
      user_agent: navigator.userAgent,
      connection_type: navigator.connection ? navigator.connection.effectiveType : 'unknown'
    })

    // Also send individual metric events for easier GA4 configuration
    window.gtag('event', `web_vital_${metric.name.toLowerCase()}`, {
      value: value,
      metric_rating: metric.rating,
      metric_id: metric.id,
      page_location: window.location.href
    })

    // Send as a Google Analytics 4 measurement event
    window.gtag('event', 'timing_complete', {
      name: metric.name,
      value: value
    })
  }
}

// Function to send a summary of all metrics when the page is unloaded
function sendMetricsSummary(metrics) {
  if (typeof window.gtag === 'function' && metrics.length > 0) {
    const summary = metrics.reduce((acc, metric) => {
      acc[`${metric.name.toLowerCase()}_value`] = Math.round(
        metric.name === 'CLS' ? metric.value * 1000 : metric.value
      )
      acc[`${metric.name.toLowerCase()}_rating`] = metric.rating
      return acc
    }, {})

    window.gtag('event', 'web_vitals_summary', {
      ...summary,
      page_location: window.location.href,
      page_title: document.title
    })
  }
}

// Function to initialize Web Vitals tracking
function initWebVitals() {
  // Only initialize if we're in the browser environment
  if (typeof window === 'undefined') return

  // Prevent multiple initializations
  if (window.webVitalsInitialized) {
    console.log('Web Vitals already initialized')
    return
  }

  console.log('Initializing Web Vitals tracking...')
  window.webVitalsInitialized = true

  const metrics = []

  // Enhanced callback to also store metrics for summary
  const enhancedCallback = (metric) => {
    console.log('Web Vitals Metric:', metric)
    sendToGoogleAnalytics(metric)

    // Update or add metric to our tracking array
    const existingIndex = metrics.findIndex(m => m.name === metric.name)
    if (existingIndex >= 0) {
      metrics[existingIndex] = metric
    } else {
      metrics.push(metric)
    }
  }

  // Function to start tracking once gtag is available
  const startTracking = () => {
    console.log('Starting Web Vitals tracking with gtag:', typeof window.gtag === 'function')

    // Core Web Vitals metrics
    onCLS(enhancedCallback)  // Cumulative Layout Shift
    onFCP(enhancedCallback)  // First Contentful Paint
    onINP(enhancedCallback)  // Interaction to Next Paint (replaces FID)
    onLCP(enhancedCallback)  // Largest Contentful Paint
    onTTFB(enhancedCallback) // Time to First Byte

    // Send summary on page unload
    window.addEventListener('beforeunload', () => {
      sendMetricsSummary(metrics)
    })

    // Also send summary on visibility change (for mobile)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        sendMetricsSummary(metrics)
      }
    })
  }

  // Check if gtag is available
  const checkGtag = () => {
    if (typeof window.gtag === 'function') {
      startTracking()
    } else {
      // Retry after a short delay if gtag is not ready yet
      setTimeout(checkGtag, 100)
    }
  }

  // Start checking for gtag availability
  checkGtag()
}

// Also initialize on DOMContentLoaded as a fallback
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWebVitals)
} else {
  // DOM is already ready, initialize immediately
  initWebVitals()
}
